// Loacker websites: where the domains live, where their redirects are and who manages them.
// Source: "Domain Redirects & Web Routing – IT Documentation", verified 2026-11-03.

export const MAIN_SITE = "https://loacker-recycling.com";

export const IP_ADINO = "85.236.51.72";
export const IP_VM63 = "20.234.210.63";
export const IP_VM108 = "20.160.25.108";

/** Workspace and backup folder of the internal IT on VM63 */
export const BACKUP_DIR = "/home/loackercc/backups_emmanuel_dns";

export type DnsAuthority = "adino" | "azure" | "unknown";
/** A = .htaccess on Adino Plesk · B = nginx 301 on VM63 · C = WordPress shows content · main = loacker-recycling.com */
export type Scenario = "A" | "B" | "C" | "main" | "unknown";

export interface Domain {
  id: string;
  domain: string;
  dns: DnsAuthority;
  /** IP of the A record */
  a?: string;
  aNote?: string;
  scenario: Scenario;
  /** File in /etc/nginx/sites-available on VM63 */
  nginxFile?: string;
  ssl: string;
  managedBy: string;
  /** Path on loacker-recycling.com */
  target?: string;
  targetNote?: string;
  forge: boolean;
  note?: string;
}

export const domains: Domain[] = [
  {
    id: "d-loacker",
    domain: "loacker-recycling.com",
    dns: "azure",
    a: IP_VM63,
    scenario: "main",
    nginxFile: "loacker-recycling.com",
    ssl: "Forge",
    managedBy: "Forge / agency",
    forge: true,
    note: "Main WordPress; final destination of all redirects",
  },
  {
    id: "d-kuster",
    domain: "kuster-recycling.ch",
    dns: "adino",
    a: IP_ADINO,
    scenario: "A",
    ssl: "Plesk (Let's Encrypt)",
    managedBy: "Internal IT (Plesk)",
    target: "/ch/location/kuster-recycling-sg/",
    forge: false,
    note: "Two .htaccess rules: ^$ for the root and ^.+$ for everything else",
  },
  {
    id: "d-weiss",
    domain: "weiss-recycling.at",
    dns: "adino",
    a: IP_VM63,
    aNote: "*.weiss-recycling.at",
    scenario: "B",
    nginxFile: "weiss-recycling.at",
    ssl: "Forge (/etc/nginx/ssl/weiss-recycling.at/…)",
    managedBy: "Forge file, redirect edited by internal IT",
    target: "/at/location/weiss-sbg/",
    forge: true,
    note: "Whole domain → one page",
  },
  {
    id: "d-eggenberger",
    domain: "eggenbergerag.li",
    dns: "adino",
    a: IP_VM63,
    aNote: "apex + www",
    scenario: "B",
    nginxFile: "eggenbergerag.li",
    ssl: "Forge (/etc/nginx/ssl/eggenbergerag.li/…)",
    managedBy: "Forge file, redirects edited by internal IT",
    target: "/ch/location/loacker-ostschweiz-recycling-sg/",
    targetNote: "Home page; other old URLs have their own location = … 301 (URL-by-URL map)",
    forge: true,
    note: "Paths that are not mapped show the Loacker WordPress under the old domain",
  },
  {
    id: "d-asr",
    domain: "asr.ch",
    dns: "azure",
    a: IP_VM63,
    aNote: "@ = A record, www = CNAME → asr.ch",
    scenario: "B",
    nginxFile: "asr.ch",
    ssl: "certbot (/etc/letsencrypt/live/asr.ch/, expires 2027-02-01, auto-renew)",
    managedBy: "Internal IT (manual, not in Forge)",
    target: "/ch/location/asr-recycling-sh/",
    forge: false,
    note: "Exception: DNS is in Azure, not Adino. Invisible to Forge",
  },
  {
    id: "d-laendleerde",
    domain: "laendleerde.at",
    dns: "adino",
    a: IP_VM63,
    aNote: "apex + www",
    scenario: "C",
    nginxFile: "laendleerde.at",
    ssl: "Forge",
    managedBy: "Forge / agency",
    forge: true,
    note: "No redirect: WordPress shows its content under laendleerde.at",
  },
  {
    id: "d-ls",
    domain: "ls-recycling.at",
    dns: "unknown",
    scenario: "unknown",
    nginxFile: "ls-recycling.at",
    ssl: "Forge (likely)",
    managedBy: "Forge / agency",
    forge: true,
    note: "To verify: DNS, A record, scenario and target",
  },
  {
    id: "d-telltex",
    domain: "tell-tex.com",
    dns: "unknown",
    scenario: "unknown",
    nginxFile: "tell-tex.com",
    ssl: "Forge (likely)",
    managedBy: "Forge / agency",
    forge: true,
    note: "To verify: DNS, A record, scenario and target",
  },
];

export const DNS_PROVIDERS: Record<Exclude<DnsAuthority, "unknown">, { name: string; ns: string[]; panel: string; how: string[] }> = {
  adino: {
    name: "Adino DNS (nsentry)",
    ns: ["ns1.profiwebspace.at", "ns2.profiwebspace.at"],
    panel: "Adino nsentry web panel",
    how: [
      "Sign in to the Adino nsentry panel.",
      "Open the domain and its DNS records.",
      "Change only the A record of @ (and www or *). Leave MX, TXT, mail, autodiscover and SIP records untouched.",
      "Save. With TTL 300 the change spreads in about 5 minutes.",
    ],
  },
  azure: {
    name: "Azure DNS (Public DNS zones)",
    ns: ["ns1-02.azure-dns.com", "ns2-02.azure-dns.net", "ns3-02.azure-dns.org", "ns4-02.azure-dns.info"],
    panel: "Azure Portal → DNS zones (subscription webapp1)",
    how: [
      "Azure Portal → DNS zones → open the domain → Recordsets.",
      "Open the @ A record. If “Alias record set” is Yes, set it to No – an alias points to an Azure resource, not to an IP.",
      "Enter the IP address and save. Make www a CNAME to the domain (or an A record with the same IP).",
      "Leave MX, TXT and mail records untouched.",
    ],
  },
};

export interface Leftover {
  text: string;
  target: string;
}

/** Rules and resources that exist but have no effect, because DNS points elsewhere */
export const leftovers: Leftover[] = [
  { text: "asr.ch records in nsentry (Adino): asr.ch NS point to Azure DNS", target: "dns-adino" },
  { text: ".htaccess redirect rules of eggenbergerag.li in Plesk: the domain points to VM63", target: "p-eggenberger" },
  { text: ".htaccess of laendleerde.at in Plesk (only DirectoryIndex): the domain points to VM63", target: "p-laendleerde" },
  { text: "Azure DNS zone laendleerde.at (NS + SOA only): laendleerde.at NS point to Adino", target: "dns-azure" },
  { text: "VM108 20.160.25.108 (old ASR website): no DNS record points to it anymore", target: "vm108-web" },
];

export const openItems: Leftover[] = [
  { text: "Run sudo certbot renew --dry-run on VM63 (asr.ch)", target: "s-asr" },
  { text: "Inform the agency that asr.ch was added manually (ideally they add it to Forge)", target: "forge" },
  { text: "Document ls-recycling.at and tell-tex.com (DNS, A record, scenario, target)", target: "d-ls" },
  { text: "Decide on the shutdown of VM108 and its public IP", target: "vm108-web" },
  { text: "eggenbergerag.li: unmapped paths show WordPress under the old domain – add a catch-all location / 301", target: "s-eggenberger" },
];

export const RULES = [
  "Only the rule on the server the DNS points to is active. A rule anywhere else has no effect.",
  "Always run sudo nginx -t before a reload. One broken file stops nginx for every site on VM63.",
  "Forge may overwrite manual edits on weiss, eggenberger, laendleerde, loacker-recycling.com, ls-recycling and tell-tex. Keep a backup.",
  "Never remove the # FORGE CONFIG / # FORGE SSL lines in Forge-created files.",
  "HTTPS needs a certificate for the redirecting domain itself. The loacker-recycling.com certificate does not cover other domains.",
  "Inform the external agency in writing about every manual change on VM63.",
  "When editing DNS, never touch MX, SPF/TXT, autodiscover, mail or SIP records.",
];
